import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, ArrowRight, TrendingUp, TrendingDown, CheckCircle2, AlertTriangle, Minus } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, YAxis } from 'recharts';

const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

const fmt = (v) => {
  if (v === null || v === undefined) return '-';
  return new Intl.NumberFormat('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
};

export default function KPIDashboardPage() {
  useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/kpi-dashboard', { params: { year } });
      setData(res.data);
    } catch { toast.error('Errore caricamento dashboard'); } finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <div className="p-8 text-center text-slate-400">Caricamento dashboard...</div>;

  return (
    <div className="space-y-5" data-testid="kpi-dashboard-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="dashboard-title">Dashboard KPI</h1>
          <p className="text-sm text-slate-500 mt-0.5">Panoramica indicatori aziendali</p>
        </div>
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-1">
          <button onClick={() => setYear(y => y - 1)} className="p-1.5 hover:bg-slate-100 rounded" data-testid="year-prev"><ChevronLeft className="w-4 h-4 text-slate-600" /></button>
          <span className="text-sm font-bold text-slate-900 min-w-[4rem] text-center" data-testid="year-display">{year}</span>
          <button onClick={() => setYear(y => y + 1)} className="p-1.5 hover:bg-slate-100 rounded" data-testid="year-next"><ChevronRight className="w-4 h-4 text-slate-600" /></button>
        </div>
      </div>

      {/* IND001 - Revenue Card */}
      <RevenueCard data={data.ind001} year={year} />

      {/* IND002 - Personnel Card */}
      <PersonnelCard data={data.ind002} year={year} />

      {/* IND003 - Customer NC Card */}
      <NCCustomerCard data={data.ind003} year={year} />

      {/* IND004 - Internal Scrap Card */}
      <ScrapCard data={data.ind004} year={year} />
    </div>
  );
}

function StatusBadge({ onTarget }) {
  if (onTarget === null || onTarget === undefined) return <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-400 bg-slate-100 px-2 py-1 rounded-full"><Minus className="w-3 h-3" />Dati insufficienti</span>;
  if (onTarget) return <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full" data-testid="status-on-target"><CheckCircle2 className="w-3 h-3" />A obiettivo</span>;
  return <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 px-2 py-1 rounded-full" data-testid="status-off-target"><AlertTriangle className="w-3 h-3" />Sotto obiettivo</span>;
}

function Sparkline({ data: points, dataKey, color }) {
  const filtered = points.filter(p => p[dataKey] !== null && p[dataKey] !== undefined);
  if (filtered.length < 2) return <div className="h-16 flex items-center justify-center text-xs text-slate-300">Nessun dato</div>;
  return (
    <ResponsiveContainer width="100%" height={64}>
      <LineChart data={points}>
        <YAxis hide domain={['auto', 'auto']} />
        <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={false} connectNulls />
      </LineChart>
    </ResponsiveContainer>
  );
}

function RevenueCard({ data: d, year }) {
  const trend = getTrend(d.months.map(m => m.actual));
  const sparkData = d.months.map((m, i) => ({ name: MONTHS[i], actual: m.actual, target: m.target }));

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="kpi-card-ind001">
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <span className="font-mono text-xs font-semibold bg-slate-900 text-white px-2 py-0.5 rounded">{d.code}</span>
              <h2 className="text-lg font-bold text-slate-900">{d.name}</h2>
              <StatusBadge onTarget={d.on_target} />
            </div>

            <div className="grid grid-cols-4 gap-4 mb-4">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Fatturato Reale</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5" data-testid="ind001-actual">{fmt(d.total_actual)} <span className="text-xs font-normal text-slate-400">EUR</span></p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Obiettivo</p>
                <p className="text-xl font-bold text-slate-500 mt-0.5" data-testid="ind001-target">{fmt(d.total_target)} <span className="text-xs font-normal text-slate-400">EUR</span></p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Scostamento</p>
                <p className={`text-xl font-bold mt-0.5 ${d.diff >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="ind001-diff">
                  {d.diff >= 0 ? '+' : ''}{fmt(d.diff)} <span className="text-xs font-normal text-slate-400">EUR</span>
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">% Scostamento</p>
                <p className={`text-xl font-bold mt-0.5 ${d.diff_pct !== null && d.diff_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="ind001-pct">
                  {d.diff_pct !== null ? (d.diff_pct >= 0 ? '+' : '') + d.diff_pct + '%' : '-'}
                </p>
              </div>
            </div>
          </div>

          <div className="w-48 flex-shrink-0">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-500">Trend {year}</span>
              <span className={`flex items-center gap-1 text-xs font-medium ${trend.color}`}>
                <trend.icon className="w-3 h-3" />{trend.label}
              </span>
            </div>
            <Sparkline data={sparkData} dataKey="actual" color="#0f172a" />
          </div>
        </div>

        {/* Monthly mini-bars */}
        <div className="grid grid-cols-12 gap-1 mt-2" data-testid="ind001-months">
          {d.months.map((m, i) => {
            const hasData = m.actual !== null;
            const onTarget = (m.actual !== null && m.target !== null) ? m.actual >= m.target : null;
            return (
              <div key={`ind001-${MONTHS[i]}`} className="text-center">
                <div className={`h-2 rounded-full ${!hasData ? 'bg-slate-100' : onTarget ? 'bg-emerald-400' : 'bg-red-400'}`} />
                <span className="text-[10px] text-slate-400 mt-0.5 block">{MONTHS[i]}</span>
              </div>
            );
          })}
        </div>
      </div>

      <Link to={d.href} className="flex items-center justify-between px-5 py-2.5 bg-slate-50 border-t border-slate-200 hover:bg-slate-100 transition-colors group" data-testid="link-ind001">
        <span className="text-sm font-medium text-slate-600">Vai al dettaglio</span>
        <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-700 transition-colors" />
      </Link>
    </div>
  );
}

function PersonnelCard({ data: d, year }) {
  const trend = getTrendInverse(d.months.map(m => m.pct));
  const sparkData = d.months.map((m, i) => ({ name: MONTHS[i], pct: m.pct, target: m.target }));

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="kpi-card-ind002">
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <span className="font-mono text-xs font-semibold bg-slate-900 text-white px-2 py-0.5 rounded">{d.code}</span>
              <h2 className="text-lg font-bold text-slate-900">{d.name}</h2>
              <StatusBadge onTarget={d.on_target} />
            </div>

            <div className="grid grid-cols-4 gap-4 mb-4">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">% Media Annua</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5" data-testid="ind002-avg">{d.avg_pct !== null ? d.avg_pct + '%' : '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Target Medio</p>
                <p className="text-xl font-bold text-slate-500 mt-0.5" data-testid="ind002-target">{d.avg_target !== null ? d.avg_target + '%' : '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Differenza</p>
                <p className={`text-xl font-bold mt-0.5 ${d.diff !== null ? (d.diff <= 0 ? 'text-emerald-600' : 'text-red-600') : 'text-slate-400'}`} data-testid="ind002-diff">
                  {d.diff !== null ? (d.diff >= 0 ? '+' : '') + d.diff + '%' : '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Stato</p>
                <p className="mt-0.5" data-testid="ind002-status">
                  {d.on_target === true && <span className="text-xl font-bold text-emerald-600">OK</span>}
                  {d.on_target === false && <span className="text-xl font-bold text-red-600">Critico</span>}
                  {d.on_target === null && <span className="text-xl font-bold text-slate-400">-</span>}
                </p>
              </div>
            </div>
          </div>

          <div className="w-48 flex-shrink-0">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-500">Trend {year}</span>
              <span className={`flex items-center gap-1 text-xs font-medium ${trend.color}`}>
                <trend.icon className="w-3 h-3" />{trend.label}
              </span>
            </div>
            <Sparkline data={sparkData} dataKey="pct" color="#0f172a" />
          </div>
        </div>

        {/* Monthly mini-bars */}
        <div className="grid grid-cols-12 gap-1 mt-2" data-testid="ind002-months">
          {d.months.map((m, i) => {
            const hasData = m.pct !== null;
            const onTarget = (m.pct !== null && m.target !== null) ? m.pct <= m.target : null;
            return (
              <div key={`ind002-${MONTHS[i]}`} className="text-center">
                <div className={`h-2 rounded-full ${!hasData ? 'bg-slate-100' : onTarget ? 'bg-emerald-400' : 'bg-red-400'}`} />
                <span className="text-[10px] text-slate-400 mt-0.5 block">{MONTHS[i]}</span>
              </div>
            );
          })}
        </div>
      </div>

      <Link to={d.href} className="flex items-center justify-between px-5 py-2.5 bg-slate-50 border-t border-slate-200 hover:bg-slate-100 transition-colors group" data-testid="link-ind002">
        <span className="text-sm font-medium text-slate-600">Vai al dettaglio</span>
        <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-700 transition-colors" />
      </Link>
    </div>
  );
}

function getTrend(values) {
  const recent = values.filter(v => v !== null).slice(-3);
  if (recent.length < 2) return { label: 'N/D', icon: Minus, color: 'text-slate-400' };
  let ups = 0, downs = 0;
  for (let i = 1; i < recent.length; i++) {
    if (recent[i] > recent[i - 1]) ups++;
    else if (recent[i] < recent[i - 1]) downs++;
  }
  if (ups > downs) return { label: 'Crescita', icon: TrendingUp, color: 'text-emerald-600' };
  if (downs > ups) return { label: 'Calo', icon: TrendingDown, color: 'text-red-600' };
  return { label: 'Stabile', icon: Minus, color: 'text-amber-600' };
}

function getTrendInverse(values) {
  const recent = values.filter(v => v !== null).slice(-3);
  if (recent.length < 2) return { label: 'N/D', icon: Minus, color: 'text-slate-400' };
  let ups = 0, downs = 0;
  for (let i = 1; i < recent.length; i++) {
    if (recent[i] > recent[i - 1]) ups++;
    else if (recent[i] < recent[i - 1]) downs++;
  }
  if (ups > downs) return { label: 'In aumento', icon: TrendingUp, color: 'text-red-600' };
  if (downs > ups) return { label: 'In calo', icon: TrendingDown, color: 'text-emerald-600' };
  return { label: 'Stabile', icon: Minus, color: 'text-amber-600' };
}

function NCCustomerCard({ data: d, year }) {
  const trend = getTrendInverse(d.months.map(m => m.count));
  const sparkData = d.months.map((m, i) => ({ name: MONTHS[i], count: m.count > 0 ? m.count : null }));
  const fmtC = (v) => v === null || v === undefined ? '-' : new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="kpi-card-ind003">
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <span className="font-mono text-xs font-semibold bg-slate-900 text-white px-2 py-0.5 rounded">{d.code}</span>
              <h2 className="text-lg font-bold text-slate-900">{d.name}</h2>
              <StatusBadge onTarget={d.on_target} />
            </div>
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Totale NC</p>
                <p className={`text-xl font-bold mt-0.5 ${d.total_count > 0 ? 'text-red-600' : 'text-emerald-600'}`} data-testid="ind003-count">{d.total_count}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Costo Totale</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5" data-testid="ind003-cost">{fmtC(d.total_cost)} <span className="text-xs font-normal text-slate-400">EUR</span></p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Costo Medio</p>
                <p className="text-xl font-bold text-slate-500 mt-0.5" data-testid="ind003-avg">{fmtC(d.avg_cost)} <span className="text-xs font-normal text-slate-400">EUR</span></p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Obiettivo</p>
                <p className="text-xl font-bold text-slate-500 mt-0.5">0 NC</p>
              </div>
            </div>
          </div>
          <div className="w-48 flex-shrink-0">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-500">Trend {year}</span>
              <span className={`flex items-center gap-1 text-xs font-medium ${trend.color}`}><trend.icon className="w-3 h-3" />{trend.label}</span>
            </div>
            <Sparkline data={sparkData} dataKey="count" color="#ef4444" />
          </div>
        </div>
        <div className="grid grid-cols-12 gap-1 mt-2" data-testid="ind003-months">
          {d.months.map((m, i) => (
            <div key={`ind003-${MONTHS[i]}`} className="text-center">
              <div className={`h-2 rounded-full ${m.count === 0 ? 'bg-emerald-400' : 'bg-red-400'}`} />
              <span className="text-[10px] text-slate-400 mt-0.5 block">{MONTHS[i]}</span>
            </div>
          ))}
        </div>
      </div>
      <Link to={d.href} className="flex items-center justify-between px-5 py-2.5 bg-slate-50 border-t border-slate-200 hover:bg-slate-100 transition-colors group" data-testid="link-ind003">
        <span className="text-sm font-medium text-slate-600">Vai al dettaglio</span>
        <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-700 transition-colors" />
      </Link>
    </div>
  );
}

function ScrapCard({ data: d, year }) {
  const trend = getTrendInverse(d.months.map(m => m.scrap_pct));
  const sparkData = d.months.map((m, i) => ({ name: MONTHS[i], pct: m.scrap_pct }));
  const fmtN = (v) => v === null || v === undefined ? '-' : new Intl.NumberFormat('it-IT').format(v);

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="kpi-card-ind004">
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <span className="font-mono text-xs font-semibold bg-slate-900 text-white px-2 py-0.5 rounded">{d.code}</span>
              <h2 className="text-lg font-bold text-slate-900">{d.name}</h2>
              <StatusBadge onTarget={d.on_target} />
            </div>
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Pezzi Prodotti</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5" data-testid="ind004-produced">{fmtN(d.total_produced)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Pezzi Scartati</p>
                <p className={`text-xl font-bold mt-0.5 ${d.total_scrapped > 0 ? 'text-red-600' : 'text-emerald-600'}`} data-testid="ind004-scrapped">{fmtN(d.total_scrapped)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">% Scarto</p>
                <p className={`text-xl font-bold mt-0.5 ${d.avg_scrap_pct !== null && d.avg_scrap_pct > 5 ? 'text-red-600' : d.avg_scrap_pct > 2 ? 'text-amber-600' : 'text-emerald-600'}`} data-testid="ind004-pct">
                  {d.avg_scrap_pct !== null ? d.avg_scrap_pct + '%' : '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Obiettivo</p>
                <p className="text-xl font-bold text-slate-500 mt-0.5">&lt; 5%</p>
              </div>
            </div>
          </div>
          <div className="w-48 flex-shrink-0">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-500">Trend {year}</span>
              <span className={`flex items-center gap-1 text-xs font-medium ${trend.color}`}><trend.icon className="w-3 h-3" />{trend.label}</span>
            </div>
            <Sparkline data={sparkData} dataKey="pct" color="#ef4444" />
          </div>
        </div>
        <div className="grid grid-cols-12 gap-1 mt-2" data-testid="ind004-months">
          {d.months.map((m, i) => {
            const hasData = m.produced > 0;
            const onTarget = hasData ? (m.scrap_pct !== null ? m.scrap_pct < 5 : true) : null;
            return (
              <div key={`ind004-${MONTHS[i]}`} className="text-center">
                <div className={`h-2 rounded-full ${!hasData ? 'bg-slate-100' : onTarget ? 'bg-emerald-400' : 'bg-red-400'}`} />
                <span className="text-[10px] text-slate-400 mt-0.5 block">{MONTHS[i]}</span>
              </div>
            );
          })}
        </div>
      </div>
      <Link to={d.href} className="flex items-center justify-between px-5 py-2.5 bg-slate-50 border-t border-slate-200 hover:bg-slate-100 transition-colors group" data-testid="link-ind004">
        <span className="text-sm font-medium text-slate-600">Vai al dettaglio</span>
        <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-700 transition-colors" />
      </Link>
    </div>
  );
}
